import { Authenticator, OAuthClientCredentials } from '@nextforce/api'
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as SecureStore from 'expo-secure-store'
import { Platform } from 'react-native'
import { safeErrorMessage } from 'shared/error'
import { buildStorageKey } from 'shared/storage'
import { DEVICE_TOKEN_KEY } from './device_token'

export class MobileAppAuthenticator extends Authenticator {
  private setAccessToken: (token: string | undefined) => void
  public onLoadTokensError: (e: Error) => void
  constructor(
    baseURL: string,
    oauthClientCredentials: OAuthClientCredentials,
    setAccessToken: (token: string | undefined) => void,
    onLoadTokensError: (e: Error) => void
  ) {
    super(baseURL, oauthClientCredentials)
    this.setAccessToken = setAccessToken
    this.onLoadTokensError = onLoadTokensError
  }

  async loadTokens() {
    let tokensString: string | null = null
    this.setAuthenticationStatus('loading')

    if (Platform.OS === 'web') {
      this.userTokens = undefined
      this.setAccessToken(undefined)
      try {
        await AsyncStorage.removeItem(buildStorageKey(this.TOKENS_KEY))
      } catch (error) {
        console.error('Error removing web tokens:', safeErrorMessage(error))
      }
      return
    }

    const maxRetries = 3
    let attempt = 0

    while (attempt < maxRetries) {
      if (attempt > 0) {
        await new Promise(resolve =>
          setTimeout(resolve, Math.pow(2, attempt) * 1000)
        )
      }
      try {
        const isSecureStorageAvailable = await SecureStore.isAvailableAsync()
        if (isSecureStorageAvailable) {
          tokensString = await SecureStore.getItemAsync(
            buildStorageKey(this.TOKENS_KEY)
          )
        }

        if (tokensString) {
          this.userTokens = JSON.parse(tokensString)
          this.setAuthenticationStatus('loggedIn')
          this.setAccessToken(this.userTokens?.accessToken)
          return
        } else {
          this.userTokens = undefined
          this.setAuthenticationStatus('loggedOut')
          this.setAccessToken(undefined)
          return
        }
      } catch (error) {
        console.error(
          `Error getting tokens (attempt ${attempt + 1}):`,
          safeErrorMessage(error)
        )
        attempt++
        if (attempt >= maxRetries) {
          return Promise.reject(error)
        }
      }
    }
  }

  async saveTokens(): Promise<void> {
    if (Platform.OS === 'web') {
      this.setAccessToken(undefined)

      if (this.userTokens?.page !== undefined) {
        try {
          await AsyncStorage.setItem(
            buildStorageKey(this.PAGE_KEY),
            JSON.stringify({
              page: this.userTokens.page,
            })
          )
        } catch (error) {
          console.error('Error saving web page key:', safeErrorMessage(error))
        }
      }

      return
    }

    this.setAccessToken(this.userTokens?.accessToken)

    const isSecureStorageAvailable = await SecureStore.isAvailableAsync()

    if (isSecureStorageAvailable) {
      try {
        await SecureStore.setItemAsync(
          buildStorageKey(this.TOKENS_KEY),
          JSON.stringify(this.userTokens)
        )
      } catch (error) {
        console.error(
          'Error saving token to secure storage:',
          safeErrorMessage(error)
        )
      }
    }
  }

  async deleteTokens(): Promise<void> {
    this.setAccessToken(undefined)
    if (Platform.OS === 'web') {
      this.userTokens = undefined
      this.setAuthenticationStatus('loggedOut')
      try {
        await AsyncStorage.removeItem(buildStorageKey(this.TOKENS_KEY))
        await AsyncStorage.removeItem(buildStorageKey(this.PAGE_KEY))
      } catch (error) {
        console.error('Error deleting web tokens:', safeErrorMessage(error))
      }
      return
    }

    const isSecureStorageAvailable = await SecureStore.isAvailableAsync()

    if (isSecureStorageAvailable) {
      return SecureStore.deleteItemAsync(
        buildStorageKey(this.TOKENS_KEY)
      ).catch((error: unknown) =>
        console.error('Error deleting tokens:', safeErrorMessage(error))
      )
    }
  }

  async refreshUserAccessToken(): Promise<string> {
    if (Platform.OS === 'web') {
      throw new Error(
        'Refresh token flow is not supported on web cookie sessions'
      )
    }

    return super.refreshUserAccessToken().then(accessToken => {
      this.setAccessToken(accessToken)
      this.saveTokens().catch(e => null)
      return accessToken
    })
  }

  onRefreshAccessTokenFailure(): void {
    this.setAccessToken(undefined)
    if (Platform.OS !== 'web') {
      AsyncStorage.multiRemove([
        buildStorageKey(DEVICE_TOKEN_KEY),
        buildStorageKey(this.TOKENS_KEY),
      ]).catch(e => null)
    }
  }

  private readonly TOKENS_KEY = 'tokens'
  private readonly PAGE_KEY = 'page'
}
